// Client-side exclude-self faceting for the reporting dashboard's six
// account-level filters (Brand / Group / Industry / AM / Country / Account).
//
// The server ships one relationship dataset — `accountFacets`, one row per
// ATTRIBUTED account with its brand/group/industry/AM/country. Every account-
// level option list is derived from these rows in memory: picking Brand narrows
// Group / Industry / AM / Country / Account to only what exists under that brand,
// and vice-versa. Standard exclude-self faceting — a filter's option list
// reflects all the OTHER selected filters but not its own (so it stays editable).
//
// Type + Currency deliberately do NOT cascade (Type is campaign-level — an
// account runs many objectives; Currency is left full for v1), so they aren't
// facets here. Pure + total; the UI just maps the output to <MultiSelectFilter>.

import type { ReportingAccountFacet } from '../types/reporting';

export type AccountFacetKey = 'brand' | 'group' | 'industry' | 'am' | 'country' | 'account';

// The subset of the filter selection the faceting reads (a structural subset of
// ReportingFilterSelection, so the bar can pass its selection straight in).
export interface FacetSelection {
  brandAccountIds: string[];
  groupAccountIds: string[];
  industries: string[];
  accountManagerIds: string[];
  countries: string[];
  accountIds: string[];
}

export interface FacetOption {
  id: string;
  name: string;
}

interface FacetConfig {
  // The row's value for this facet — both the id stored in the selection AND the
  // value matched against it. For plain-string facets (Industry/Country) the id
  // IS the string; for id+name facets (Brand/Group/AM/Account) it's the stable id.
  id: (r: ReportingAccountFacet) => string | null;
  // The row's display name for this facet (falls back to the id when null).
  name: (r: ReportingAccountFacet) => string | null;
  // Which selection field this facet binds to.
  selected: (s: FacetSelection) => string[];
}

const FACETS: Record<AccountFacetKey, FacetConfig> = {
  brand: { id: (r) => r.brandAccountId, name: (r) => r.brandName, selected: (s) => s.brandAccountIds },
  group: { id: (r) => r.groupAccountId, name: (r) => r.groupName, selected: (s) => s.groupAccountIds },
  industry: { id: (r) => r.industry, name: (r) => r.industry, selected: (s) => s.industries },
  am: {
    id: (r) => r.accountManagerId,
    name: (r) => r.accountManagerName,
    selected: (s) => s.accountManagerIds,
  },
  country: { id: (r) => r.country, name: (r) => r.country, selected: (s) => s.countries },
  account: { id: (r) => r.accountId, name: (r) => r.accountName, selected: (s) => s.accountIds },
};

const ALL_KEYS = Object.keys(FACETS) as AccountFacetKey[];

// The available options for `facetKey`, given the current selection of the OTHER
// account-level filters (exclude-self). A row matches a facet's selection when
// its value for that facet is in the selected set; an empty selection is no
// constraint; a null/empty value never matches a non-empty selection. From the
// surviving rows, the distinct id+name options for `facetKey` (null/empty
// dropped, deduped by id, sorted by name).
export function availableOptions(
  accountFacets: ReportingAccountFacet[],
  selection: FacetSelection,
  facetKey: AccountFacetKey,
): FacetOption[] {
  const others = ALL_KEYS.filter((k) => k !== facetKey);
  const rows = accountFacets.filter((r) =>
    others.every((k) => {
      const sel = FACETS[k].selected(selection);
      if (sel.length === 0) return true; // no constraint
      const v = FACETS[k].id(r);
      return !!v && sel.includes(v); // null/empty never matches a non-empty selection
    }),
  );

  const cfg = FACETS[facetKey];
  const byId = new Map<string, string>();
  for (const r of rows) {
    const id = cfg.id(r);
    if (!id) continue; // drop null/empty
    if (!byId.has(id)) byId.set(id, cfg.name(r) ?? id);
  }
  return [...byId.entries()]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}
